/**
 * Which handles a transform grid shows, by entity type.
 *
 * | Entity Type      | Translation | Rotation | Uniform Scale | Non-Uniform Resize |
 * |------------------|-------------|----------|---------------|-------------------|
 * | Circle           | ✓           | ✗        | ✓             | ✗                 |
 * | Arc              | ✓           | ✗        | ✓             | ✗                 |
 * | Ellipse          | ✓           | ✓        | ✓             | ✓                 |
 * | Text/MText       | ✓           | ✗        | ✗             | ✗                 |
 * | Polyline         | ✓           | ✗        | ✗             | ✗                 |
 * | Rectangle        | ✓           | ✓        | ✗             | ✓                 |
 * | BlockReference   | ✓           | ✓        | ✓             | ✗                 |
 * | PhotoMarker      | ✓           | ✓        | ✓             | ✗                 |
 * | Hatch            | ✓           | ✗        | ✗             | ✗                 |
 * | Dimension        | dimension line only (DimensionLineHandle)             |
 * | Multiple/Unknown | ✓           | ✗        | ✗             | ✗                 |
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { CommandHistory } from "../commands/CommandHistory";
import type { IHandle } from "./handles/IHandle";
import type { KeyPoints, Point3D, ResizeAxes } from "./types";
import { HandlePosition } from "./types";
import { TranslationHandle } from "./handles/TranslationHandle";
import { GroupTranslationHandle } from "./handles/GroupTranslationHandle";
import { RotationHandle } from "./handles/RotationHandle";
import { ScaleHandle } from "./handles/ScaleHandle";
import { ResizeHandle } from "./handles/ResizeHandle";
import { BlockReferenceResizeHandle } from "./handles/BlockReferenceResizeHandle";
import { BlockReferenceScaleHandle } from "./handles/BlockReferenceScaleHandle";
import { VertexHandle } from "./handles/VertexHandle";
import { MoveSideHandle, type EdgeIndex } from "./handles/MoveSideHandle";
import { AddLeaderHandle } from "./handles/AddLeaderHandle";
import {
  DeleteLeaderHandle,
  getMLeaderArrowTips,
} from "./handles/DeleteLeaderHandle";
import { MLeaderTextHandle } from "./handles/MLeaderTextHandle";
import { DimensionLineHandle } from "./handles/DimensionLineHandle";
import { getDimensionPoints } from "../dimensions";
import { buildDynamicBlockHandles } from "./dynBlockGridHandles";
import { isDynamicBlockRef } from "../blocks/dynamic";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle } from "../core/oda";
import {
  getTransformableEntityType,
  TransformableEntityType,
} from "../entities/detection";
import { getTextEntityProperties } from "../leaders/conversion";

/** What the handle builders need from the grid. */
export interface GridHandlesContext {
  viewer: DRDrawingViewer;
  history: CommandHistory;
  module: DrawingModuleType;
  entityIds: DrawingModule.OdDbObjectId[];
  keyPoints: KeyPoints;
}

export interface GridHandles {
  handles: IHandle[];
  /** Whether the bounding box is drawn (false for polylines, texts…). */
  showPerimeter: boolean;
}

/**
 * Group grid of a multiple selection: the shared bounding box and the handle
 * that moves everything, the entities' own handles being on their own grids.
 */
export function buildGroupHandles(ctx: GridHandlesContext): GridHandles {
  return {
    handles: [
      new GroupTranslationHandle(
        ctx.viewer,
        ctx.history,
        ctx.entityIds,
        ctx.keyPoints,
      ),
    ],
    showPerimeter: true,
  };
}

/** Handles of the grid of one entity (or of an untyped set of entities). */
export function buildEntityHandles(ctx: GridHandlesContext): GridHandles {
  const { viewer, module, entityIds } = ctx;
  const isGroup = entityIds.length > 1;

  // Get entity type (only for single selection)
  const entityType = isGroup
    ? TransformableEntityType.Unknown
    : getTransformableEntityType(module, entityIds[0]);

  // A dynamic block shows ONLY its own property handles (plus the perimeter):
  // mixing them with the generic transform grips makes it unclear which grip
  // drives the block's parameters and which one just moves/scales the insert.
  if (
    !isGroup &&
    entityType === TransformableEntityType.BlockReference &&
    isDynamicBlockRef(module, entityIds[0])
  ) {
    // The translation handle is the one exception: moving the whole insert must
    // stay available, otherwise a dynamic block could no longer be repositioned.
    return {
      handles: [translation(ctx), ...buildDynamicBlockHandles(ctx)],
      showPerimeter: true,
    };
  }

  // A linear dimension only offers its dimension-line handle: moving the whole
  // dimension would detach it from the points it measures.
  if (entityType === TransformableEntityType.Dimension) {
    const dimension = getDimensionPoints(module, entityIds[0]);
    return {
      handles: dimension
        ? [new DimensionLineHandle(viewer, ctx.history, entityIds[0], dimension)]
        : [translation(ctx)],
      showPerimeter: false,
    };
  }

  const handles: IHandle[] = [];

  // Translation handle - always available
  // For MText/MLeader, use text center (not entity bounding box center which includes leader lines)
  // For MLeader, use MLeaderTextHandle which only moves text (keeps arrow tips fixed)
  if (entityType === TransformableEntityType.MLeader) {
    const textProps = getTextEntityProperties(viewer, entityIds[0]);
    if (textProps) {
      const textCenter: Point3D = {
        x: (textProps.textExtent.min.x + textProps.textExtent.max.x) / 2,
        y: (textProps.textExtent.min.y + textProps.textExtent.max.y) / 2,
        z: 0,
      };
      handles.push(
        new MLeaderTextHandle(viewer, ctx.history, entityIds[0], textCenter),
      );
    } else {
      handles.push(translation(ctx));
    }
  } else if (entityType === TransformableEntityType.MText) {
    // MText: TranslationHandle at bottom-left corner of text bounding box
    const textProps = getTextEntityProperties(viewer, entityIds[0]);
    if (textProps) {
      const textBottomLeft: Point3D = {
        x: textProps.textExtent.min.x,
        y: textProps.textExtent.min.y,
        z: 0,
      };
      handles.push(
        translation(ctx, { ...ctx.keyPoints, center: textBottomLeft }),
      );
    } else {
      handles.push(translation(ctx));
    }
  } else {
    handles.push(translation(ctx));
  }

  // Configure handles based on entity type
  // Also determine if perimeter should be shown (only for resizable entities)
  let showPerimeter = true;
  switch (entityType) {
    case TransformableEntityType.Circle:
    case TransformableEntityType.Arc:
      // Translation + uniform scale only (no rotation, no non-uniform resize)
      handles.push(scale(ctx));
      break;

    case TransformableEntityType.Ellipse:
      // Full transform support: rotation + resize + uniform scale
      handles.push(
        rotation(ctx),
        ...resizeHandles(ctx, ResizeHandle),
        scale(ctx),
      );
      break;

    case TransformableEntityType.Text:
    case TransformableEntityType.Hatch:
      // Translation only - no perimeter needed
      showPerimeter = false;
      break;

    case TransformableEntityType.MText:
      // Translation + AddLeader handle
      handles.push(new AddLeaderHandle(viewer, ctx.history, entityIds[0]));
      showPerimeter = false;
      break;

    case TransformableEntityType.Polyline:
      // Translation + vertex editing handles - no perimeter needed
      handles.push(...vertexHandles(ctx));
      showPerimeter = false;
      break;

    case TransformableEntityType.Rectangle:
      // Translation + rotation + side handles (direct vertex manipulation)
      // Using MoveSideHandle instead of ResizeHandle because matrix-based
      // scaling doesn't work correctly for polylines in ODA
      handles.push(scale(ctx), rotation(ctx), ...moveSideHandles(ctx));
      break;

    case TransformableEntityType.BlockReference:
      // Plain (non-dynamic) block: dynamic ones returned earlier with their own handles.
      // Translation + rotation + uniform scale + non-uniform resize
      // Uses BlockReference-specific handles which work around ODA transformBy() limitation
      handles.push(
        rotation(ctx),
        ...resizeHandles(ctx, BlockReferenceResizeHandle),
        new BlockReferenceScaleHandle(
          viewer,
          ctx.history,
          entityIds,
          ctx.keyPoints,
          HandlePosition.BottomRight,
        ),
      );
      break;

    case TransformableEntityType.PhotoMarker:
      // Translation + rotation only: photo markers keep their aspect ratio
      handles.push(rotation(ctx));
      break;

    case TransformableEntityType.MLeader: {
      // MLeader: AddLeader handle + DeleteLeader handles at each arrow tip
      handles.push(new AddLeaderHandle(viewer, ctx.history, entityIds[0]));
      const arrowTips = getMLeaderArrowTips(viewer, entityIds[0]);
      for (const { leaderLineIndex, arrowTip } of arrowTips) {
        handles.push(
          new DeleteLeaderHandle(
            viewer,
            ctx.history,
            getEntityHandle(entityIds[0]),
            leaderLineIndex,
            arrowTip,
          ),
        );
      }
      showPerimeter = false;
      break;
    }

    case TransformableEntityType.RasterImage:
      // RasterImage: Translation + non-uniform resize
      handles.push(...resizeHandles(ctx, ResizeHandle), scale(ctx));
      break;

    default:
      // Unknown or multiple selection: translation only (no rotation for groups)
      break;
  }

  return { handles, showPerimeter };
}

function translation(
  ctx: GridHandlesContext,
  keyPoints: KeyPoints = ctx.keyPoints,
): IHandle {
  return new TranslationHandle(
    ctx.viewer,
    ctx.history,
    ctx.entityIds,
    keyPoints,
  );
}

function rotation(ctx: GridHandlesContext): IHandle {
  return new RotationHandle(
    ctx.viewer,
    ctx.history,
    ctx.entityIds,
    ctx.keyPoints,
  );
}

/** Uniform scale handle (offset from bottom-right corner). */
function scale(ctx: GridHandlesContext): IHandle {
  return new ScaleHandle(
    ctx.viewer,
    ctx.history,
    ctx.entityIds,
    ctx.keyPoints,
    HandlePosition.BottomRight,
  );
}

/** Corner (both axes) then edge (single axis) resize handles: position, anchor, axes. */
const RESIZE_LAYOUT: [HandlePosition, HandlePosition, ResizeAxes?][] = [
  [HandlePosition.TopLeft, HandlePosition.BottomRight],
  [HandlePosition.TopRight, HandlePosition.BottomLeft],
  [HandlePosition.BottomLeft, HandlePosition.TopRight],
  [HandlePosition.BottomRight, HandlePosition.TopLeft],
  [HandlePosition.TopMiddle, HandlePosition.BottomMiddle, { y: true }],
  [HandlePosition.BottomMiddle, HandlePosition.TopMiddle, { y: true }],
  [HandlePosition.LeftMiddle, HandlePosition.RightMiddle, { x: true }],
  [HandlePosition.RightMiddle, HandlePosition.LeftMiddle, { x: true }],
];

/**
 * Non-uniform resize handles. Block references use BlockReferenceResizeHandle,
 * which works around ODA's transformBy() limitation for non-uniform scaling by
 * using setScaleFactors() and setPosition() directly.
 */
function resizeHandles(
  ctx: GridHandlesContext,
  Handle: typeof ResizeHandle | typeof BlockReferenceResizeHandle,
): IHandle[] {
  return RESIZE_LAYOUT.map(
    ([position, anchor, axes]) =>
      new Handle(
        ctx.viewer,
        ctx.history,
        ctx.entityIds,
        ctx.keyPoints,
        position,
        anchor,
        axes,
      ),
  );
}

/**
 * Rectangle resizing by direct vertex manipulation: a handle at the middle of
 * each edge (edge i connects vertex i to vertex (i+1)%4). Works for both
 * axis-aligned and rotated rectangles.
 */
function moveSideHandles(ctx: GridHandlesContext): IHandle[] {
  if (ctx.entityIds.length !== 1) return [];
  return ([0, 1, 2, 3] as EdgeIndex[]).map(
    (edge) =>
      new MoveSideHandle(ctx.viewer, ctx.history, ctx.entityIds[0], edge),
  );
}

/** Polyline editing: a handle at each vertex. */
function vertexHandles(ctx: GridHandlesContext): IHandle[] {
  if (ctx.entityIds.length !== 1) return [];

  const entityId = ctx.entityIds[0];
  const polyline = openAs(entityId, ctx.module.OdDbPolyline, 0);
  const numVerts = polyline.numVerts();
  const handles: IHandle[] = [];

  for (let i = 0; i < numVerts; i++) {
    const point2d = new ctx.module.OdGePoint2d(0, 0);
    polyline.getPointAt(i, point2d);

    const worldPos: Point3D = { x: point2d.x, y: point2d.y, z: 0 };
    deleteObj(point2d);

    handles.push(
      new VertexHandle(ctx.viewer, ctx.history, entityId, i, worldPos),
    );
  }
  deleteObj(polyline);
  return handles;
}
